/**
 * ForensicInsightsCard - Phase 3.2 Accountant Dashboard Enhancement
 * 
 * Shows forensic analysis results including:
 * - Benford's Law analysis
 * - Statistical outlier detection
 * - Data integrity score
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';
import {
  Search,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Shield,
  TrendingUp,
  Loader2,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';

interface ForensicInsight {
  type: 'benford' | 'outlier' | 'integrity' | 'pattern';
  title: string;
  finding: string;
  severity: 'normal' | 'warning' | 'anomaly';
  score?: number;
  details?: Record<string, any>;
}

interface ForensicInsightsCardProps {
  reportId: string;
  className?: string;
}

const INSIGHT_ICONS = {
  benford: BarChart3,
  outlier: TrendingUp,
  integrity: Shield,
  pattern: Search
};

const SEVERITY_STYLES = {
  normal: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    icon: 'text-green-600',
    badge: 'bg-green-100 text-green-700'
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    icon: 'text-yellow-600',
    badge: 'bg-yellow-100 text-yellow-700'
  },
  anomaly: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: 'text-red-600',
    badge: 'bg-red-100 text-red-700'
  }
};

export default function ForensicInsightsCard({ reportId, className = '' }: ForensicInsightsCardProps) {
  const { getToken } = useAuth();
  const [insights, setInsights] = useState<ForensicInsight[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (reportId) fetchInsights();
  }, [reportId]);

  const fetchInsights = async () => {
    setLoading(true);
    setError(false);
    try {
      const token = await getToken();
      const data = await api.get(`/api/v1/dashboard/forensic-insights/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` },
        showErrorToast: false
      });
      setInsights(data.insights || []);
    } catch (err) {
      console.debug('Forensic insights API not available');
      setInsights([]);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const overallStatus = insights.some(i => i.severity === 'anomaly') ? 'anomaly' :
                        insights.some(i => i.severity === 'warning') ? 'warning' : 'normal';

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Search className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-semibold text-gray-900">Forensic Insights</h3>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${SEVERITY_STYLES[overallStatus].badge}`}>
            {overallStatus === 'normal' ? 'All Clear' : 
             overallStatus === 'warning' ? 'Review Needed' : 'Issues Found'}
          </span>
        </div>
      </div>

      {/* Insights list */}
      <div className="divide-y divide-gray-100">
        {error && insights.length === 0 ? (
          <div className="p-6 text-center">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-500">Forensic insights unavailable</p>
            <button
              onClick={fetchInsights}
              className="mt-2 text-xs text-blue-600 hover:text-blue-700"
            >
              Retry
            </button>
          </div>
        ) : insights.length === 0 ? (
          <div className="p-6 text-center">
            <Search className="w-8 h-8 mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-500">No forensic insights yet</p>
          </div>
        ) : insights.map((insight, index) => {
          const Icon = INSIGHT_ICONS[insight.type] || Search;
          const styles = SEVERITY_STYLES[insight.severity];
          const isExpanded = expanded === insight.type;

          return (
            <div key={index} className={`p-4 ${styles.bg}`}>
              <button
                onClick={() => setExpanded(isExpanded ? null : insight.type)}
                className="w-full text-left"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <div className={`p-2 rounded-lg bg-white ${styles.icon} mr-3`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{insight.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{insight.finding}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    {insight.score !== undefined && (
                      <span className={`text-lg font-bold mr-2 ${styles.icon}`}>
                        {insight.type === 'integrity' ? `${insight.score}%` : insight.score}
                      </span>
                    )}
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </button>

              {/* Expanded details */}
              {isExpanded && insight.details && (
                <div className="mt-4 p-3 bg-white rounded-lg">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">Details</h5>
                  <pre className="text-xs text-gray-600 overflow-x-auto">
                    {JSON.stringify(insight.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="p-4 bg-gray-50 border-t border-gray-100 rounded-b-xl">
        <p className="text-xs text-gray-500 flex items-center">
          <Info className="w-4 h-4 mr-1" />
          Forensic analysis helps identify potential data anomalies that may require explanation.
        </p>
      </div>
    </div>
  );
}
